import React, { Fragment, useEffect, useState } from 'react'
import classes from './ProductVariantTable.module.css'

const ProductVariantTable = ({ productData, setProductData, singleProductData }) => {
    const { variantInfo, generalInfo } = productData
    const { price, discount, details } = productData?.variantInfo.quantityAndPrice
    const [attributes, setAttributes] = useState([])
    const [variants, setVariants] = useState([])

    const [firstVariantName, secondVariantName] = variantInfo?.selectedVariants?.map((variant) => variant.variantName)
    // Helper function to calculate the discounted price
    const calculateDiscountedPrice = (price, discountPercentage) => {
        return Math.round(price - price * (discountPercentage / 100))
    }

    const getAtributeRowData = (attribute, index) => {
        const attributesArray = attribute.includes('|')
            ? attribute.split('|').map((attr) => attr.trim())
            : [attribute.trim()]
        const [firstAttribute, secondAttribute] = attributesArray
        return {
            type: firstVariantName,
            value: firstAttribute,
            type2: secondAttribute ? secondVariantName : '',
            value2: secondAttribute || '',
        }
    }

    const handleProductPrice = (e, index) => {
        const { name, value } = e.target
        const numericValue = Number(value)

        setVariants((prevVariants) =>
            prevVariants.map((variant, idx) =>
                idx === index
                    ? {
                          ...variant,
                          [name]: numericValue,
                          b2cdPrice:
                              name === 'b2cp' || name === 'b2cdPercentage'
                                  ? calculateDiscountedPrice(
                                        name === 'b2cp' ? numericValue : variant.b2cp,
                                        name === 'b2cdPercentage' ? numericValue : variant.b2cdPercentage
                                    )
                                  : variant.b2cdPrice,
                          b2bdPrice:
                              name === 'b2bp' || name === 'b2bdPercentage'
                                  ? calculateDiscountedPrice(
                                        name === 'b2bp' ? numericValue : variant.b2bp,
                                        name === 'b2bdPercentage' ? numericValue : variant.b2bdPercentage
                                    )
                                  : variant.b2bdPrice,
                      }
                    : variant
            )
        )
    }

    useEffect(() => {
        const [firstVariantAttributes, secondVariantAttributes] = variantInfo.selectedVariants?.map(
            (variant) => variant.attributes
        )
        if (variantInfo.selectedVariants.length > 1) {
            const multipliedAttributes = firstVariantAttributes
                .map((attribute1) => {
                    return secondVariantAttributes.map((attribute2) => `${attribute1} | ${attribute2}`)
                })
                .flat()
            setAttributes(multipliedAttributes)
        } else {
            setAttributes(firstVariantAttributes)
        }
    }, [variantInfo.selectedVariants, generalInfo])

    useEffect(() => {
        if (variants.length > 0) {
            if (variantInfo.separetePrice) {
                setProductData((prev) => ({
                    ...prev,
                    variantInfo: { ...prev.variantInfo, separetePriceVariants: variants },
                }))
            } else {
                setProductData((prev) => ({
                    ...prev,
                    variantInfo: { ...prev.variantInfo, variants },
                }))
            }
        }
    }, [variants])

    useEffect(() => {
        if (!variantInfo.separetePrice) {
            const newVariants = attributes?.map((attribute, index) => {
                const attributesObjectsData = getAtributeRowData(attribute, index)
                const { value, value2 } = attributesObjectsData
                const existingVariant = value2
                    ? singleProductData?.ProductVariant.find(
                          (v) => v.variant_value === value && v.variant_value2 === value2
                      )
                    : singleProductData?.ProductVariant.find((v) => v.variant_value === value)

                const isVariantNewlyCreated =
                    (value2 && !singleProductData.ProductVariant.some((v) => v.variant_value2)) ||
                    !singleProductData.ProductVariant[index]

                if (isVariantNewlyCreated) {
                    setProductData((prev) => ({
                        ...prev,
                        variantInfo: { ...prev.variantInfo, isCreateVariant: true },
                    }))
                }

                return {
                    ...attributesObjectsData,
                    quantity: existingVariant?.quantity || 0,
                    cop: details.cop,
                    b2cp: price.b2cp || 0,
                    b2cdPercentage: price.b2cd || 0,
                    b2cdPrice: price.b2cp ? calculateDiscountedPrice(price.b2cp, price.b2cd) : 0,
                    b2bp: price.b2bp || 0,
                    b2bdPercentage: price.b2bd || 0,
                    b2bdPrice: price.b2cp ? calculateDiscountedPrice(price.b2bp, price.b2bd) : 0,
                    id: existingVariant?.id ? existingVariant.id : index,
                }
            })

            setVariants(newVariants)
        } else if (variantInfo.separetePrice) {
            const newVariants = attributes?.map((attribute, index) => {
                const attributesObjectsData = getAtributeRowData(attribute, index)
                const { value, value2 } = attributesObjectsData
                const existingVariant = value2
                    ? singleProductData?.ProductVariant.find(
                          (v) => v.variant_value === value && v.variant_value2 === value2
                      )
                    : singleProductData?.ProductVariant.find((v) => v.variant_value === value)

                const isVariantNewlyCreated =
                    (value2 && !singleProductData.ProductVariant.some((v) => v.variant_value2)) ||
                    !singleProductData.ProductVariant[index]

                if (isVariantNewlyCreated) {
                    setProductData((prev) => ({
                        ...prev,
                        variantInfo: { ...prev.variantInfo, isCreateVariant: true },
                    }))
                }
                return {
                    ...attributesObjectsData,
                    quantity: existingVariant?.quantity || 0,
                    cop: existingVariant?.manufacture_cost || 0,
                    b2cp: existingVariant?.retail_price || 0,
                    b2cdPercentage: existingVariant?.retail_discount_percentage || 0,
                    b2cdPrice: existingVariant?.final_retail_price || 0,
                    b2bp: existingVariant?.wholesale_price ?? 0,
                    b2bdPercentage: existingVariant?.wholesale_discount_percentage || 0,
                    b2bdPrice: existingVariant?.final_wholesale_price || 0,
                    id: existingVariant?.id ? existingVariant.id : index,
                }
            })

            setVariants(newVariants)
        }
    }, [
        attributes,
        firstVariantName,
        secondVariantName,
        variantInfo.separetePrice,
        variantInfo.quantityAndPrice,
        singleProductData,
    ])

    return (
        <Fragment>
            {variants.length > 0 && (
                <Fragment>
                    <div className={classes.productTable}>
                        <table>
                            <thead>
                                <tr>
                                    <th>
                                        Variant
                                        <span>
                                            {firstVariantName} {secondVariantName && ' | ' + secondVariantName}
                                        </span>
                                    </th>
                                    <th>Cost of Product</th>

                                    {generalInfo.isB2c && (
                                        <Fragment>
                                            <th>Retail Price</th>
                                            <th>Discount (%) Retail Price</th>
                                            <th>Retail Price After Discount</th>
                                        </Fragment>
                                    )}
                                    {generalInfo.isB2b && (
                                        <Fragment>
                                            <th>Wholesale Price</th>
                                            <th>Discount (%) Wholesale Price</th>
                                            <th>Wholesale Price After Discount</th>
                                        </Fragment>
                                    )}
                                    <th>Product Quantity</th>
                                </tr>
                            </thead>
                            <tbody>
                                <button className={classes.displayVariantButton}>Display Variant</button>
                                {variants?.map((variant, index) => {
                                    const isDisabled = !variantInfo.separetePrice

                                    const {
                                        type,
                                        value,
                                        type2,
                                        value2,
                                        cop,
                                        b2cp,
                                        b2cdPercentage,
                                        b2cdPrice,
                                        b2bp,
                                        b2bdPercentage,
                                        b2bdPrice,
                                        quantity,
                                    } = variant
                                    return (
                                        <tr key={index}>
                                            <td>
                                                {value} {type2 && ` | ${value2}`}
                                            </td>
                                            <td>
                                                <input
                                                    type="number"
                                                    value={cop}
                                                    name="cop"
                                                    onChange={(e) => handleProductPrice(e, index)}
                                                    disabled={isDisabled}
                                                />
                                            </td>
                                            {generalInfo.isB2c && (
                                                <>
                                                    <td>
                                                        <input
                                                            type="number"
                                                            value={b2cp}
                                                            name="b2cp"
                                                            onChange={(e) => handleProductPrice(e, index)}
                                                            disabled={isDisabled}
                                                        />
                                                    </td>
                                                    <td>
                                                        <input
                                                            type="number"
                                                            className={classes.input}
                                                            value={b2cdPercentage}
                                                            name="b2cdPercentage"
                                                            onChange={(e) => handleProductPrice(e, index)}
                                                            disabled={isDisabled}
                                                        />
                                                    </td>
                                                    <td>
                                                        <input
                                                            type="number"
                                                            value={b2cdPrice}
                                                            name="b2cdPrice"
                                                            onChange={(e) => handleProductPrice(e, index)}
                                                            disabled
                                                        />
                                                    </td>
                                                </>
                                            )}
                                            {generalInfo.isB2b && (
                                                <>
                                                    <td>
                                                        <input
                                                            type="number"
                                                            value={b2bp}
                                                            name="b2bp"
                                                            onChange={(e) => handleProductPrice(e, index)}
                                                            disabled={isDisabled}
                                                        />
                                                    </td>
                                                    <td>
                                                        <input
                                                            type="number"
                                                            className={classes.input}
                                                            value={b2bdPercentage}
                                                            name="b2bdPercentage"
                                                            onChange={(e) => handleProductPrice(e, index)}
                                                            disabled={isDisabled}
                                                        />
                                                    </td>
                                                    <td>
                                                        <input
                                                            type="number"
                                                            value={b2bdPrice}
                                                            name="b2bdPrice"
                                                            onChange={(e) => handleProductPrice(e, index)}
                                                            disabled
                                                        />
                                                    </td>
                                                </>
                                            )}
                                            <td>
                                                <input
                                                    type="number"
                                                    value={quantity}
                                                    name="quantity"
                                                    onChange={(e) => handleProductPrice(e, index)}
                                                />
                                            </td>
                                        </tr>
                                    )
                                })}
                            </tbody>
                        </table>
                    </div>

                    {variants.reduce((total, variant) => total + variant.quantity, 0) > 0 && (
                        <div className={classes.footer}>
                            <p className="total">
                                Total Product Quantity :
                                <span> {variants.reduce((total, variant) => total + variant.quantity, 0)}</span>
                            </p>
                        </div>
                    )}
                </Fragment>
            )}
        </Fragment>
    )
}

export default ProductVariantTable
